import { useId, useState } from 'react';
import { CONTENT_PAGE_LIMITS, CONTENT_PAGE_SEO } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import {
  autoDescription,
  searchTitle,
  seoScore,
  type CheckLevel,
  type Language,
  type PageDraft,
  type PageText,
  type SeoCheck,
} from '@/lib/content-pages';
import { storefrontOrigin, storefrontPath } from '@/lib/storefront-url';
import { ToggleField } from '@/components/fields';
import { AlertIcon, CheckIcon, CopyIcon, GlobeIcon, MonitorIcon, PhoneIcon } from '@/components/icons';
import { Section, Segmented } from '@/components/page';
import { Button, cx } from '@/components/ui';
import { MediaField } from '../home-layout/fields';

/**
 * How a page is found and shared (ADR-058), one language at a time: what a
 * search result shows and a preview of it, what a shared link shows and a
 * preview of that, and whether search engines may list the page. Its
 * checklist sits in the side panel beside these fields, so it answers as
 * they are typed.
 */
export function SeoPanel({
  page,
  language,
  slug,
  storeName,
  onChange,
}: {
  page: PageDraft;
  language: Language;
  slug: string;
  storeName: string;
  onChange: (next: PageDraft) => void;
}) {
  const t = useT();
  const text = page[language];
  const setText = (patch: Partial<PageText>) => onChange({ ...page, [language]: { ...text, ...patch } });
  const fallbackDescription = autoDescription(text.body);
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <div className="flex flex-col gap-5">
      <Section title={t('seoSearchTitle')} subtitle={t('seoSearchHint')}>
        <div className="flex flex-col gap-5">
          <SearchPreview page={page} language={language} slug={slug} storeName={storeName} />

          <MeteredField
            label={t('metaTitle')}
            hint={t('seoMetaTitleHint', { store: storeName || '—' })}
            value={text.metaTitle}
            placeholder={text.title}
            dir={dir}
            language={language}
            measured={searchTitle(text, storeName).length}
            range={CONTENT_PAGE_SEO.title}
            limit={CONTENT_PAGE_LIMITS.metaTitle}
            onChange={(metaTitle) => setText({ metaTitle })}
          />
          <MeteredField
            multiline
            label={t('metaDescription')}
            hint={text.metaDescription.trim() ? t('seoMetaDescriptionHint') : t('seoMetaDescriptionAuto')}
            value={text.metaDescription}
            placeholder={fallbackDescription}
            dir={dir}
            language={language}
            measured={(text.metaDescription.trim() || fallbackDescription).length}
            range={CONTENT_PAGE_SEO.description}
            limit={CONTENT_PAGE_LIMITS.metaDescription}
            onChange={(metaDescription) => setText({ metaDescription })}
            action={
              !text.metaDescription.trim() && fallbackDescription ? (
                <Button variant="ghost" size="sm" onClick={() => setText({ metaDescription: fallbackDescription })}>
                  {t('seoUseOpening')}
                </Button>
              ) : null
            }
          />
          <AddressRow language={language} slug={slug} />
        </div>
      </Section>

      <Section title={t('seoShareTitle')} subtitle={t('seoShareHint')}>
        <div className="flex flex-col gap-5">
          <MediaField
            kind="image"
            label={t('seoShareImage')}
            hint={t('seoShareImageHint')}
            value={page.image}
            portrait={false}
            onChange={(image) => onChange({ ...page, image: image ?? null })}
          />
          <SocialPreview page={page} language={language} storeName={storeName} />
        </div>
      </Section>

      <Section title={t('seoEnginesTitle')}>
        <ToggleField
          label={t('seoIndexLabel')}
          hint={page.noindex ? t('seoIndexOffHint') : t('seoIndexOnHint')}
          checked={!page.noindex}
          onChange={(index) => onChange({ ...page, noindex: !index })}
        />
      </Section>

    </div>
  );
}

// ─── Fields ──────────────────────────────────────────────────────────────────

/**
 * A field measured against what reads well in a search result: a bar that
 * fills toward the sweet spot and turns amber either side of it, and red past
 * what the API will take. What is measured is what will show — the title with
 * the store's name after it, the opening words where nothing is written.
 */
function MeteredField({
  label,
  hint,
  value,
  placeholder,
  dir,
  language,
  measured,
  range,
  limit,
  multiline = false,
  action,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  placeholder: string;
  dir: 'rtl' | 'ltr';
  language: Language;
  measured: number;
  range: { min: number; max: number };
  limit: number;
  multiline?: boolean;
  action?: React.ReactNode;
  onChange: (next: string) => void;
}) {
  const t = useT();
  const id = useId();
  const locale = useUiStore((state) => state.locale);
  const over = value.length > limit;
  const tone = over || measured === 0 ? 'danger' : measured < range.min || measured > range.max ? 'warning' : 'success';
  const fill = Math.min(100, (measured / (range.max * 1.25)) * 100);
  const Control = multiline ? 'textarea' : 'input';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end justify-between gap-3">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
        </label>
        {action}
      </div>
      <Control
        id={id}
        dir={dir}
        lang={language}
        rows={multiline ? 3 : undefined}
        value={value}
        placeholder={placeholder}
        aria-invalid={over || undefined}
        onChange={(event: { target: { value: string } }) => onChange(event.target.value)}
        className={cx(
          'min-h-10 w-full rounded-control border bg-surface px-3 text-sm text-ink transition-colors placeholder:text-ink-faint focus:border-brand-500',
          multiline && 'py-2',
          over ? 'border-danger' : 'border-line',
        )}
      />
      <div className="flex items-center gap-3">
        <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-surface-sunken">
          {/* The sweet spot, marked on the track. */}
          <span
            aria-hidden
            className="absolute inset-y-0 bg-success/15"
            style={{ insetInlineStart: `${(range.min / (range.max * 1.25)) * 100}%`, width: `${((range.max - range.min) / (range.max * 1.25)) * 100}%` }}
          />
          <span
            aria-hidden
            className={cx(
              'absolute inset-y-0 start-0 rounded-full transition-all',
              tone === 'success' ? 'bg-success' : tone === 'warning' ? 'bg-warning' : 'bg-danger',
            )}
            style={{ width: `${fill}%` }}
          />
        </div>
        <span data-numeric className={cx('text-[11px] tabular-nums', tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-success')}>
          {t('seoMeter', { count: count(measured, locale), min: count(range.min, locale), max: count(range.max, locale) })}
        </span>
      </div>
      <p className="text-xs text-ink-muted">{hint}</p>
    </div>
  );
}

function AddressRow({ language, slug }: { language: Language; slug: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const origin = storefrontOrigin();
  const path = storefrontPath(language, `/pages/${slug}`);
  const full = `${origin ?? ''}${path}`;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{t('pageAddress')}</span>
      <div className="flex items-center gap-2 rounded-control border border-line bg-surface-sunken/60 px-3 py-2">
        <GlobeIcon className="size-4 shrink-0 text-ink-faint" />
        <span dir="ltr" className="min-w-0 flex-1 truncate text-start text-sm text-ink">
          {origin ? <span className="text-ink-faint">{origin}</span> : null}
          {path}
        </span>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(full).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1600);
            });
          }}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-ink-muted hover:text-ink"
        >
          {copied ? <CheckIcon className="size-3.5 text-success" /> : <CopyIcon className="size-3.5" />}
          {copied ? t('copied') : t('copy')}
        </button>
      </div>
      <p className="text-xs text-ink-muted">{t('pageAddressHint')}</p>
    </div>
  );
}

// ─── Previews ────────────────────────────────────────────────────────────────

/** A search result as Google draws one — on a desktop or a phone. */
function SearchPreview({ page, language, slug, storeName }: { page: PageDraft; language: Language; slug: string; storeName: string }) {
  const t = useT();
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const text = page[language];
  const title = searchTitle(text, storeName);
  const description = text.metaDescription.trim() || autoDescription(text.body);
  const origin = storefrontOrigin();
  const host = origin ? origin.replace(/^https?:\/\//, '') : storeName || t('pageYourStore');
  const crumbs = storefrontPath(language, `/pages/${slug}`).split('/').filter(Boolean);
  const date = page.updatedAt
    ? new Intl.DateTimeFormat(language === 'ar' ? 'ar-SA-u-nu-latn' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(page.updatedAt))
    : null;
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-ink-muted">{t('seoPreviewGoogle')}</span>
        <Segmented
          label={t('seoPreviewDevice')}
          value={device}
          onChange={setDevice}
          items={[
            { id: 'desktop', label: t('seoDesktop') },
            { id: 'mobile', label: t('seoMobile') },
          ]}
        />
      </div>
      <div className="rounded-card border border-line bg-white p-4 text-start dark:bg-[#1f1f1f]">
        <div
          dir={dir}
          lang={language}
          className={cx('font-[arial,sans-serif]', device === 'mobile' ? 'max-w-[22rem]' : 'max-w-[37.5rem]')}
        >
          <div className="flex items-center gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-[#dadce0] bg-[#f1f3f4] text-xs font-semibold text-[#5f6368]">
              {(storeName || host).trim().charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm text-[#202124] dark:text-[#dadce0]">{storeName || host}</p>
              <p dir="ltr" className="truncate text-start text-xs text-[#4d5156] dark:text-[#bdc1c6]">
                {[origin ?? host, ...crumbs].join(' › ')}
              </p>
            </div>
          </div>
          <p
            className={cx(
              'mt-2 text-[#1a0dab] hover:underline dark:text-[#99c3ff]',
              device === 'mobile' ? 'line-clamp-2 text-lg leading-snug' : 'truncate text-xl leading-snug',
            )}
          >
            {title || <span className="text-[#70757a]">{t('pageUntitled')}</span>}
          </p>
          <p className={cx('mt-1 text-sm leading-[1.58] text-[#4d5156] dark:text-[#bdc1c6]', device === 'mobile' ? 'line-clamp-3' : 'line-clamp-2')}>
            {date ? <span className="text-[#70757a]">{date} — </span> : null}
            {description || <span className="text-[#70757a]">{t('seoNoDescription')}</span>}
          </p>
        </div>
      </div>
      <p className="flex items-center gap-1.5 text-[11px] text-ink-faint">
        {device === 'desktop' ? <MonitorIcon className="size-3.5" /> : <PhoneIcon className="size-3.5" />}
        {t('seoPreviewNote')}
      </p>
    </div>
  );
}

/** A link to the page as a chat or a social feed draws it. */
function SocialPreview({ page, language, storeName }: { page: PageDraft; language: Language; storeName: string }) {
  const t = useT();
  const text = page[language];
  const image = imageUrl(page.image);
  const origin = storefrontOrigin();
  const host = origin ? origin.replace(/^https?:\/\//, '') : storeName;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-ink-muted">{t('seoPreviewSocial')}</span>
      <div dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language} className="w-full max-w-md overflow-hidden rounded-card border border-line bg-surface">
        <div className="aspect-[1.91/1] bg-surface-sunken">
          {image ? (
            <img src={image} alt="" className="size-full object-cover" />
          ) : (
            <div className="flex size-full flex-col items-center justify-center gap-1 px-4 text-center">
              <span className="text-sm font-semibold text-ink">{storeName}</span>
              <span className="text-[11px] text-ink-faint">{t('seoNoImage')}</span>
            </div>
          )}
        </div>
        <div className="border-t border-line px-3 py-2.5">
          <p dir="ltr" className="truncate text-start text-[11px] text-ink-faint uppercase">{host}</p>
          <p className="mt-0.5 truncate text-sm font-semibold text-ink">{text.metaTitle.trim() || text.title || t('pageUntitled')}</p>
          <p className="mt-0.5 line-clamp-2 text-xs text-ink-muted">{text.metaDescription.trim() || autoDescription(text.body)}</p>
        </div>
      </div>
    </div>
  );
}

function imageUrl(value: unknown): string | undefined {
  if (typeof value === 'string') return mediaUrl(value);
  if (value && typeof value === 'object') {
    const url = (value as { url?: unknown }).url;
    if (typeof url === 'string') return mediaUrl(url);
  }
  return undefined;
}

// ─── Health ──────────────────────────────────────────────────────────────────

const LEVEL_STYLE: Record<CheckLevel, string> = {
  good: 'bg-success/12 text-success',
  warn: 'bg-warning/15 text-warning',
  bad: 'bg-danger/10 text-danger',
};

export function Checklist({ checks, limit }: { checks: SeoCheck[]; limit?: number }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const shown = limit ? checks.slice(0, limit) : checks;
  return (
    <ul className="flex flex-col gap-2">
      {shown.map((check) => (
        <li key={check.id} className="flex items-start gap-2.5 text-sm">
          <span className={cx('mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full', LEVEL_STYLE[check.level])}>
            {check.level === 'good' ? <CheckIcon className="size-3" /> : <AlertIcon className="size-3" />}
          </span>
          <span className={check.level === 'good' ? 'text-ink-muted' : 'text-ink'}>
            {t(
              check.message,
              check.values
                ? Object.fromEntries(Object.entries(check.values).map(([name, value]) => [name, typeof value === 'number' ? count(value, locale) : value]))
                : undefined,
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** A score out of 100 as a ring — green from 80, amber from 50, red below. */
export function ScoreRing({ checks, size = 56 }: { checks: SeoCheck[]; size?: number }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const score = seoScore(checks);
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const tone = score >= 80 ? 'text-success' : score >= 50 ? 'text-warning' : 'text-danger';

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} role="img" aria-label={t('seoScoreLabel', { score })}>
      <svg viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={5} className="stroke-surface-sunken" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={5}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - score / 100)}
          className={cx('stroke-current transition-all duration-500', tone)}
        />
      </svg>
      <span data-numeric className={cx('absolute inset-0 flex items-center justify-center text-sm font-bold', tone)}>
        {count(score, locale)}
      </span>
    </div>
  );
}
